/**
 * Return Form Component
 * Handles return/exchange form logic within inline panels
 */

export default function returnForm() {
  return {
    orderId: null,
    formData: {
      requestType: '',
      reasonCategory: '',
      selectedItems: {},
      quantities: {},
      reasonText: '',
      additionalNotes: '',
      termsAgreed: false,
    },
    files: [],
    errors: [],
    isSubmitting: false,

    init(orderId, initialSelectedItems = {}, initialQuantities = {}) {
      this.orderId = orderId;

      // Initialize selectedItems and quantities from PHP-provided values
      // This ensures proper Alpine.js reactivity from the start
      this.formData.selectedItems = { ...initialSelectedItems };
      this.formData.quantities = { ...initialQuantities };
    },

    toggleAll(event) {
      const isChecked = event.target.checked;
      const form = this.$el.querySelector('form');
      if (!form) return;

      const checkboxes = form.querySelectorAll('input[name*="[selected]"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = isChecked;
        const match = checkbox.name.match(/items\[(\d+)\]\[selected\]/);
        if (match) {
          const itemKey = match[1];
          this.formData.selectedItems[itemKey] = isChecked;
        }
      });
    },

    handleFileUpload(event) {
      const uploadedFiles = Array.from(event.target.files);
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'application/pdf',
      ];

      const validFiles = uploadedFiles.filter(file => {
        if (file.size > maxSize) {
          window.Notification?.error?.(window.tx('errorFileTooLarge', 'File exceeds 5MB limit').replace('%s', file.name)) ||
            alert(window.tx('errorFileTooLarge', 'File exceeds 5MB limit').replace('%s', file.name));
          return false;
        }

        if (!allowedTypes.includes(file.type)) {
          window.Notification?.error?.(window.tx('errorFileType', 'File is not a supported type').replace('%s', file.name)) ||
            alert(window.tx('errorFileType', 'File is not a supported type').replace('%s', file.name));
          return false;
        }

        return true;
      });

      this.files = [...this.files, ...validFiles];

      // Clear the input to allow re-uploading the same files
      event.target.value = '';
    },

    removeFile(index) {
      this.files.splice(index, 1);
    },

    // Sync form data from DOM (fallback if x-model doesn't sync properly)
    syncFormData() {
      // With proper x-model binding from PHP initialization,
      // this should rarely be needed, but kept as a safety fallback
      const form = this.$el.querySelector('form');
      if (!form) return;

      // Sync selectedItems from checkboxes
      const itemCheckboxes = form.querySelectorAll('input[name*="[selected]"]');
      itemCheckboxes.forEach(checkbox => {
        const match = checkbox.name.match(/items\[([^\]]+)\]\[selected\]/);
        if (match) {
          const itemKey = match[1];
          this.formData.selectedItems[itemKey] = checkbox.checked;
        }
      });

      // Sync quantities from number inputs
      const quantityInputs = form.querySelectorAll('input[name*="[quantity]"]');
      quantityInputs.forEach(input => {
        const match = input.name.match(/items\[([^\]]+)\]\[quantity\]/);
        if (match) {
          const itemKey = match[1];
          this.formData.quantities[itemKey] = parseInt(input.value) || 1;
        }
      });
    },

    validateForm() {
      // Sync form data before validation to ensure we have current values
      this.syncFormData();

      this.errors = [];

      // Check request type
      if (!this.formData.requestType) {
        this.errors.push(window.tx('errorSelectRequestType', 'Please select a request type'));
      }

      // Check reason category
      if (!this.formData.reasonCategory) {
        this.errors.push(window.tx('errorSelectReasonCategory', 'Please select a reason category'));
      }

      // Check selected items
      const selectedItemsCount = Object.values(this.formData.selectedItems).filter(
        selected => selected
      ).length;
      if (selectedItemsCount === 0) {
        this.errors.push(window.tx('errorSelectItems', 'Please select at least one item for return/exchange'));
      }

      // Check detailed reason
      if (!this.formData.reasonText || this.formData.reasonText.trim().length < 10) {
        this.errors.push(window.tx('errorDetailedReason', 'Please provide a detailed reason (at least 10 characters)'));
      }

      // Check terms agreement
      if (!this.formData.termsAgreed) {
        this.errors.push(window.tx('errorAgreeTerms', 'Please agree to the terms and conditions'));
      }

      return this.errors.length === 0;
    },

    async submitForm(event, actionUrl) {
      if (this.isSubmitting) return;

      if (!this.validateForm()) {
        // Scroll to error message
        this.$nextTick(() => {
          const errorElement = this.$el.querySelector('[x-show="errors.length > 0"]');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }

      this.isSubmitting = true;

      // Show global loader
      if (window.Loader) {
        window.Loader.show('return-form', window.tx('loadingDefault', 'Loading...'));
      }

      const form = event.target;
      const formData = new FormData(form);

      // Add files to form data
      this.files.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      // Use provided actionUrl or fallback to form.action
      const url = actionUrl || form.action;
      try {
        const response = await fetch(url, {
          method: 'POST',
          body: formData,
        });

        // Check if response is OK
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.status === 'success') {
          window.Notification?.success?.(result.message || window.tx('returnSuccess', 'Return request submitted successfully')) ||
            alert(result.message || window.tx('returnSuccess', 'Return request submitted successfully'));

          // Reset form and close panel after delay
          setTimeout(() => {
            this.resetForm();
            this.close();
          }, 2000);
        } else {
          this.errors = [result.message || window.tx('returnError', 'An error occurred while submitting your request')];
        }
      } catch (error) {
        this.errors = [`Network error: ${error.message}. Please try again.`];
      } finally {
        this.isSubmitting = false;
        // Hide global loader
        if (window.Loader) {
          window.Loader.hide('return-form');
        }
      }
    },

    resetForm() {
      this.formData = {
        requestType: '',
        reasonCategory: '',
        selectedItems: {},
        quantities: {},
        reasonText: '',
        additionalNotes: '',
        termsAgreed: false,
      };
      this.files = [];
      this.errors = [];

      // Reset form elements
      const form = this.$el.querySelector('form');
      if (form) {
        form.reset();
      }
    },

    close() {
      // Find and call parent's closeReturnForm
      // Walk up the DOM to find orderHistoryManager
      let el = this.$el;
      while (el) {
        if (el._x_dataStack) {
          const stack = el._x_dataStack;
          for (const data of stack) {
            if (typeof data.closeReturnForm === 'function') {
              data.closeReturnForm(this.orderId);
              return;
            }
          }
        }
        el = el.parentElement;
      }

      // Fallback: dispatch custom event
      this.$dispatch('close-return-form', { orderId: this.orderId });
    },
  };
}